// ImpostorBaker — Set13 P4: bake each structural variant's REAL 3D mesh straight down into one
// cell of an atlas `WebGLRenderTarget`, off the visible frame, PRE-LIT with the map's current
// light rig (§3.5, §4.4). The cell is the only thing P5's 2D pass draws — a quad samples this
// atlas instead of the mesh, so the mesh itself is never rendered live in v1.
//
// DESIGN (from the Set13 plan):
//   - PRE-LIT (§4.4): each cell stores final shaded RGBA (albedo × the terrain's diffuse shade
//     term, computed in the bake fragment shader). The light rig joins the atlas invalidation
//     signature (§3.5), so a rare light edit costs a few hundred ms re-bake — a good trade for a
//     simpler quad shader + atlas format + no normal-encoding decisions.
//   - Invalidation signature (§3.5): the kind's RESOLVED PARAMS (VALUES INCLUDED — a crown-radius
//     edit changes the variant meshes, so the atlas MUST re-bake) + variantCount + seed + the light
//     rig (azimuth/altitude/ambient/strength) + cell size. This is deliberately DISTINCT from the
//     quad program's structuralHash (which is param-VALUE-invariant — the quad shader is the same
//     regardless of the atlas content, the D10 free-variant property). The two are separate caches.
//   - Cell resolution 256² default (§3.5). 6 variants × 256² RGBA ≈ 1.6 MB.
//
// The generator (which builds the mesh from a variant's params) runs in the TerrainWorker (§4.6);
// this baker is RENDERER-side (it needs the WebGL context) and consumes the resulting meshes.
// `bakeCells` is the production path (worker-generated meshes) and `bakeKind` the single-threaded
// convenience path (derives variants + generates meshes via the registry — the GPU lane).
//
// ⭐ SET14 P4 §4.5 — the bake SHADING MODEL changed from our own pre-lit `ShaderMaterial` (which
// mirrored the terrain hillshade byte-for-byte) to **ez-tree's `MeshStandardMaterial` + real
// lights**. The atlas is STILL pre-lit RGBA (Set13 §4.4 stands); only *what computes the shading*
// changed. Rationale (goal §1.2): a 3D mode is coming and must reach ez-tree-demo quality — if the
// impostor is Lambert and the 3D near tier is PBR, the near/far crossfade visibly flattens. So the
// impostor and the future 3D near-tier mesh share ONE material: bark (`MeshStandardMaterial` with
// color/normal/roughness maps) and leaf (`MeshStandardMaterial` with the leaf sprite, DoubleSide,
// alphaTest), lit by a `DirectionalLight` + `AmbientLight` derived from the BakeLightRig.
//   - The bake output config (`toneMapping` + `outputColorSpace`) is PINNED + RESTORED (§4.10),
//     joining the viewport/scissor/pixelRatio save-restore dance — leaking bake state into the
//     live frame has bitten this system twice. MeshStandardMaterial already contains three's
//     `#include <tonemapping_fragment>` / `<colorspace_fragment>`, so it uses the live renderer's
//     mapping automatically (the future 3D tier does the same — no divergence).
//   - The transitional per-vertex `colors`/`albedo` are GONE (§4.5): the legacy bake shader
//     required a `color` attribute; textures + material tints replace it.
import * as THREE from "three";
import {deriveVariants} from "../SceneryVariants.js";
import {type ResolvedSceneryKind} from "../SceneryResolverCore.js";
import {getSceneryGenerator} from "../Generators/SceneryGeneratorRegistry.js";
import {type SceneryVariant, type SceneryMesh} from "../@SceneryTypes.js";
import {hashString32} from "../SceneryHash.js";
import {textureVersionToken} from "./SceneryTextures.js";
// Set15 P7 — the shared material builder + geometry converter, promoted out of this file (the
// bake uses it to pre-lit the atlas; the 3D near tier uses the SAME builder for its real meshes).
import {buildSceneryMaterials, partToGeometry} from "./SceneryMaterials.js";
// 2026-08-28 — bake-internal timing (mesh-gen vs offscreen render vs readback).
import {timeIt} from "../Render/SceneryTiming.js";

// TEMP DEBUG (2026-09-01) — bake a SOLID RED atlas (every cell fully red, alpha 255) to
// isolate bake vs render: if billboards show red, the bake is fine + the render path works;
// if still nothing, the problem is downstream (the billboard material/shader/depth).
// 2026-09-01 — turned back OFF now that billboards render (the root cause was the billboard
// back-facing the camera, fixed in SceneryRenderer's `az` sign; see fillKindInstances_inner).
export const BAKE_SOLID_RED_DEBUG = false;

/** The light rig the bake pre-lights with — mirrors `TerrainHeightShadingConfig`'s shading fields
 *  (azimuth/altitude/ambient/strength; slope_scale is deliberately NOT here — it applies to the
 *  GEOMETRIC terrain normal, irrelevant to a baked mesh). Every field joins the atlas signature. */
export interface BakeLightRig {
	azimuth: number;
	altitude: number;
	ambient: number;
	strength: number;
}

/** Default light rig — the terrain's `TerrainHeightShadingConfig` defaults (315/45/0.2/1.0). */
export const DEFAULT_BAKE_LIGHT_RIG: BakeLightRig = {azimuth: 315, altitude: 45, ambient: 0.2, strength: 1.0};

/** The light direction — byte-for-byte the TerrainWorker's `computeLightDir` (the terrain's
 *  `computeLightDir` convention). A unit vector pointing TOWARD the light (used by `dot(n, L)`).
 *  The bake's DirectionalLight is positioned at `bakeLightDir(rig) * d` so it shines from that
 *  direction toward the origin. */
export function bakeLightDir(rig: BakeLightRig): [number, number, number] {
	const azRad = rig.azimuth * Math.PI / 180;
	const altRad = rig.altitude * Math.PI / 180;
	return [
		Math.cos(altRad) * Math.sin(azRad),
		Math.cos(altRad) * Math.cos(azRad),
		Math.sin(altRad),
	];
}

/** Set14 P3 §4.6 — the PER-VARIANT seed: `variantSeed = hash(kindSeed, variant.index)`, injected
 *  as ez-tree's `options.seed`. Distinct variants get distinct seeds; the same `(kindSeed, index)`
 *  reproduces byte-identical geometry. It derives from `kindDef.seed`, which already joins the bake
 *  signature — so a seed change re-bakes (the "reroll does nothing" guard, §4.6). A kind with
 *  `seed == 0` and `variant.index == 0` yields `0` (the RNG's default), which is fine. */
export function variantSeed(resolved: ResolvedSceneryKind, index: number): number {
	return (hashString32(`${resolved.seed}:${index}`) >>> 0);
}

export interface SceneryBakeOpts {
	/** Per-cell resolution (square). Default 256. This sets the maximum crisp zoom (§3.5). */
	cellSize?: number;
	/** Set15 P4 (§3.8) — the bake camera PITCH (degrees BELOW the horizontal, matching
	 *  `Mod_Viewpoint.pitch`'s convention: 0 = horizontal, 90 = straight down = the 2D top-down
	 *  default).  The atlas is baked at this pitch so the 3D impostor shows the tree's true view
	 *  from the locked viewpoint rather than a top-down silhouette billboarded into a side view
	 *  (which looks badly wrong).  Because the 3D viewpoint is LOCKED, ONE extra atlas at that
	 *  pitch suffices (§3.8).  Joins the atlas signature so a pitch change re-bakes. */
	pitch?: number;
	// ── Set14 P4 §4.10 — the bake OUTPUT CONFIG joins the signature (for exactly the reason the
	// light rig does: change the tone-mapping/colour space and every previously-baked cell is on
	// the old curve while the terrain is on the new one). Defaults = three's NoToneMapping /
	// SRGB, so a caller that doesn't pin (the Node-lane signature test) still gets a stable key. ──
	/** The renderer's `toneMapping` at bake time. */
	toneMapping?: number;
	/** The renderer's `outputColorSpace` at bake time. */
	outputColorSpace?: string;
	/** The texture-readiness/version token (§4.9/§4.10): a bake taken BEFORE its textures arrived
	 *  must NOT be cached as final — the token changes when readiness flips, replacing the bake. */
	textureToken?: string;
}

export interface SceneryBakeResult {
	/** The atlas invalidation signature (§3.5) — the cache key for this bake. */
	signature: string;
	cellSize: number;
	cols: number;
	rows: number;
	atlasWidth: number;
	atlasHeight: number;
	/** Full atlas RGBA bytes (row-major, top-left origin — the P5 quad-sampler's layout). */
	pixels: Uint8ClampedArray;
	/** The variants baked, in cell order (index i → cell (i%cols, floor(i/cols))). */
	variants: SceneryVariant[];
	/** Per-cell fraction of pixels whose alpha > 0 (coverage). Parallel to `variants`. */
	coverage: number[];
	/** Half of the world-extent the whole atlas spans (metres). The quad renderer sizes each
	 *  instance's quad to `cellWorldHalfExtent * inst.scale` so the sprite is drawn at its true
	 *  baked size (the old code sized by an independently-authored `footprintRadius`, which could
	 *  disagree with the bake framing → trees drawn ~0.6× their baked size). */
	cellWorldHalfExtent: number;
	/** 2026-09-01 — the VERTICAL world half-extent (the tree's projected height at the bake
	 *  pitch = unionMaxZ·sin(pitch)). The 3D billboard quad's Z scale uses this (NOT the
	 *  horizontal `cellWorldHalfExtent`) so the quad's vertical matches the tree's projected
	 *  silhouette — at low pitch the tree is a wide-short edge-on sliver, and a square quad
	 *  sized to the horizontal extent would sample an empty vertical → the tree vanishes. The
	 *  2D path ignores this (pitch=90 → sin=1 → cellWorldHalfExtentZ = cellWorldHalfExtent, square). */
	cellWorldHalfExtentZ: number;
	/** Per-variant crown-top height in metres (the mesh's max Z), parallel to `variants`. Drives
	 *  the shadow's down-light shear (per variant × instance scale) and the tree's depth key —
	 *  the honest MEASURED silhouette height (per variant), never an authored constant. */
	variantMaxZ: number[];
}

// ── Texture type ids a kind needs (§4.9/§4.10) ───────────────────────────────────────────────
// A kind's `barkType`/`leafType` params (the flat keys from EzTreeParams) select which texture
// sets the bake attaches. These drive the texture-readiness gate + the signature's texture token.

/** Read the barkType/leafType ids from a resolved kind's params (whichever are declared). */
export function kindTextureTypeIDs(resolved: ResolvedSceneryKind): {barkTypes: string[]; leafTypes: string[]} {
	const barkTypes: string[] = [];
	const leafTypes: string[] = [];
	for (const p of resolved.params) {
		if (p.key == "barkType" && typeof p.value == "string") barkTypes.push(p.value);
		if (p.key == "leafType" && typeof p.value == "string") leafTypes.push(p.value);
	}
	return {barkTypes, leafTypes};
}

/** The atlas invalidation signature (§3.5): resolved params (VALUES INCLUDED) + variantCount +
 *  seed + the light rig + cell size + the output config (tone-mapping / colour space / texture
 *  token, §4.10). Deterministic. A change in any of these re-bakes. */
export function sceneryBakeSignature(
	resolved: ResolvedSceneryKind,
	light: BakeLightRig,
	opts: SceneryBakeOpts = {},
): string {
	const cellSize = opts.cellSize ?? 256;
	const pitch = opts.pitch ?? 90;
	const toneMapping = opts.toneMapping ?? 0;        // NoToneMapping
	const outputColorSpace = opts.outputColorSpace ?? "srgb";
	const textureToken = opts.textureToken ?? "";
	// Resolved params with their resolved VALUES — a VALUE edit (e.g. crownRadius 3.5 → 4.5)
	// changes the sampled variant meshes → the atlas must re-bake. (Contrast: the quad shader's
	// structuralHash excludes values — the quad program is value-invariant.)
	const paramStr = resolved.params
		.map(p => `${p.key}=${JSON.stringify(p.value)}/s${p.swing}/c${p.curve}`)
		.join("|");
	const canonical =
		`P:${paramStr}|N:${resolved.variantCount}|S:${resolved.seed}|` +
		`L:${light.azimuth},${light.altitude},${light.ambient},${light.strength}|C:${cellSize}|` +
		`Pitch:${pitch}|T:${toneMapping}|O:${outputColorSpace}|X:${textureToken}`;
	return hashString32(canonical).toString(16);
}

// ── The class ────────────────────────────────────────────────────────────────────────────────

/** The baker — wraps a three.js WebGLRenderer (attached to the SAME gl context the terrain
 *  renderer uses) and renders variant meshes into an offscreen atlas. */
export class ImpostorBaker {
	private renderer: THREE.WebGLRenderer;
	private opts: Required<SceneryBakeOpts>;

	/** Shared bake scene/camera + a bark mesh + a leaf mesh + lights, created lazily. */
	private camera: THREE.OrthographicCamera | null = null;
	private scene: THREE.Scene | null = null;
	private barkMesh: THREE.Mesh | null = null;
	private leafMesh: THREE.Mesh | null = null;
	private directional: THREE.DirectionalLight | null = null;
	private ambient: THREE.AmbientLight | null = null;

	constructor(renderer: THREE.WebGLRenderer, opts: SceneryBakeOpts = {}) {
		this.renderer = renderer;
		this.opts = {
			cellSize: opts.cellSize ?? 256,
			pitch: opts.pitch ?? 90,   // default: top-down (the 2D atlas — byte-identical to before)
			toneMapping: opts.toneMapping ?? 0,
			outputColorSpace: opts.outputColorSpace ?? "srgb",
			textureToken: opts.textureToken ?? "",
		};
	}

	/** The cell resolution this baker uses. */
	get cellSize(): number { return this.opts.cellSize; }

	/** The pitch this baker bakes at (degrees below horizontal; 90 = top-down). */
	get pitch(): number { return this.opts.pitch; }

	/** Set the bake camera pitch for subsequent bakes (Set15 P4 §3.8 — the locked 3D viewpoint's
	 *  pitch).  A pitch change alters the atlas content AND its signature, so the next bake
	 *  re-bakes (the cached 2D top-down atlas is replaced for the 3D session). */
	setPitch(pitch: number): void {
		// 0 = horizontal, 90 = straight down (the 2D default). Clamp to (0, 90] — a horizontal
		// (0°) bake is a degenerate edge-on view; clamp it to a small non-zero pitch instead.
		const p = Number.isFinite(pitch) ? pitch : 90;
		this.opts.pitch = Math.max(1, Math.min(90, p));
	}

	/** Update this baker's baked output-config from the LIVE renderer + a resolved kind (§4.10):
	 *  tone-mapping mode + colour space (both read live so a mid-session change re-bakes) and the
	 *  texture-readiness/version token (so a pre-texture bake is replaced, never cached). */
	private captureOutputConfig(resolved: ResolvedSceneryKind): void {
		const {barkTypes, leafTypes} = kindTextureTypeIDs(resolved);
		this.opts.toneMapping = this.renderer.toneMapping;
		this.opts.outputColorSpace = this.renderer.outputColorSpace;
		this.opts.textureToken = textureVersionToken(barkTypes, leafTypes);
	}

	/** End-to-end convenience path: derive the kind's variants, generate each mesh via the
	 *  generator registry, and bake them into one atlas. Returns the bake result. The generator is
	 *  a pure function, so running it here (single-threaded) yields the same mesh the worker would.
	 *  For the production flow (worker-generated meshes), use {@link bakeCells} instead. */
	bakeKind(resolved: ResolvedSceneryKind, light: BakeLightRig): SceneryBakeResult {
		return this.bakeKindDetailed(resolved, light).bake;
	}

	/** Set15 P7 (§3.8) — like {@link bakeKind} but ALSO returns the generated variant meshes, so
	 *  the 3D near tier can build real InstancedMeshes from them (the impostor atlas + the near
	 *  mesh share the SAME generated geometry — they differ only in resolution). The `variants`
	 *  (in the bake) pair 1:1 with `meshes`. Returns the bake + the generated meshes + the
	 *  derived variants. On an unknown generator (or empty atlas) the bake degrades to an empty
	 *  result and `meshes`/`variants` are the (empty / fallback) values. */
	bakeKindDetailed(
		resolved: ResolvedSceneryKind,
		light: BakeLightRig,
	): {bake: SceneryBakeResult; meshes: SceneryMesh[]; variants: SceneryVariant[]} {
		this.captureOutputConfig(resolved);
		const variants = deriveVariants(resolved);
		const generator = getSceneryGenerator(resolved.generatorID);
		if (generator == null) {
			// Unknown generator — degrade, never throw (the bake must skip a kind it can't build).
			const cellSize = this.opts.cellSize;
			const cols = Math.max(1, variants.length);
			return {
				bake: {
					signature: sceneryBakeSignature(resolved, light, this.opts),
					cellSize, cols, rows: 1,
					atlasWidth: cols * cellSize, atlasHeight: cellSize,
					pixels: new Uint8ClampedArray(cols * cellSize * cellSize * 4),
					variants, coverage: variants.map(()=>0),
					cellWorldHalfExtent: 1, cellWorldHalfExtentZ: 1, variantMaxZ: variants.map(()=>1),
				},
				meshes: [], variants,
			};
		}
		const meshes = timeIt("bake.meshGen", ()=>variants.map(v => generator.generate(v.params, variantSeed(resolved, v.index))));
		return {bake: this.bakeCells(resolved, meshes, variants, light), meshes, variants};
	}

	/** Bake an array of meshes (one per variant) straight down into one atlas. `meshes.length`
	 *  should equal `variants.length`. Renders each mesh into its cell, off the visible frame.
	 *  `resolved` is the source of the atlas signature (§3.5). */
	bakeCells(
		resolved: ResolvedSceneryKind,
		meshes: SceneryMesh[],
		variants: SceneryVariant[],
		light: BakeLightRig,
	): SceneryBakeResult {
		this.captureOutputConfig(resolved);
		const cellSize = this.opts.cellSize;
		const n = meshes.length;
		const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
		const rows = Math.max(1, Math.ceil(n / cols));
		const atlasW = cols * cellSize;
		const atlasH = rows * cellSize;

		this.ensureScratch();
		const camera = this.camera!;
		const barkMesh = this.barkMesh!;
		const leafMesh = this.leafMesh!;

		// ⚠ This baker shares the LIVE renderer (the R3F canvas's WebGLRenderer/context). three's
		// `setViewport`/`setScissor` write PERSISTENT renderer state that carries into subsequent
		// frames — and neither three's `render()` (for a regular camera) nor R3F resets it per
		// frame. If we left it at a cell/atlas size, the whole visible frame would render into a
		// shrunken sub-rect anchored bottom-left (the Set13 "scaled-down viewport" bug). Save the
		// viewport/scissor/scissor-test/pixelRatio state and restore it after the off-frame bake.
		// Set14 P4 §4.10 — the output CONFIG (`toneMapping`/`outputColorSpace`) joins this dance:
		// MeshStandardMaterial reads them per-render, so they must be pinned (they also join the
		// signature) and restored so the bake never leaks them into the live frame.
		const savedViewport = new THREE.Vector4().copy(this.renderer.getViewport(new THREE.Vector4()));
		const savedScissor = new THREE.Vector4().copy(this.renderer.getScissor(new THREE.Vector4()));
		const savedScissorTest = this.renderer.getScissorTest();
		// ⚠ pixelRatio: three's `setViewport(x,y,w,h)` scales the GL viewport by the renderer's
		// pixelRatio. The render target + cell viewports below are sized in UNSCALED atlas pixels
		// (cellSize² per cell), so on a high-DPI live renderer (pixelRatio 2) a non-origin cell's
		// scaled viewport (e.g. vx=256 → GL 512) overflows the atlas → that cell renders nothing.
		// Force pixel ratio 1 for the bake so viewport coords == atlas pixels, and restore it.
		const savedPixelRatio = this.renderer.getPixelRatio();
		const savedToneMapping = this.renderer.toneMapping;
		const savedOutputColorSpace = this.renderer.outputColorSpace;
		this.renderer.setPixelRatio(1);
		this.renderer.toneMapping = savedToneMapping;
		this.renderer.outputColorSpace = savedOutputColorSpace;

		// Frame the camera over the union of all meshes' X/Y (GROUND-PLANE) extents so every
		// variant is framed the SAME way (one atlas, one scale) and fills a plausible fraction of
		// its cell. The mesh is Z-up (X/Y = ground). Iterates the mesh's PARTS (Set14 §4.7).
		// ⚠ Set15 P4 (§3.8): at the 2D default pitch (90 = top-down) this is byte-identical to the
		// pre-P4 path (plain top-down ortho at +Z, camera-space == world-space). At a non-90 pitch
		// the camera is re-oriented to look down at the tree at that pitch (up = +z so the tree is
		// upright on screen, matching a 3D billboard), and the framing becomes a SQUARE spanning
		// the crown half-extent AND the full tree height (ground at the cell bottom) so a
		// billboard quad sized to `cellWorldHalfExtent` can sit its base exactly on the terrain.
		let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
		const variantMaxZ: number[] = [];
		let unionMaxZ = -Infinity;
		for (const m of meshes) {
			let maxZ = -Infinity;
			for (const part of m.parts) {
				const p = part.positions;
				for (let i = 0; i < p.length; i += 3) {
					const x = p[i], y = p[i + 1], z = p[i + 2];
					if (x < minX) minX = x;
					if (x > maxX) maxX = x;
					if (y < minY) minY = y;
					if (y > maxY) maxY = y;
					if (z > maxZ) maxZ = z;
				}
			}
			// Per-variant crown-top height (the HONEST silhouette height — feeds the shadow shear
			// + depth key). Fall back to the mesh's authored maxZ (ez-tree sets it), else 1.
			variantMaxZ.push(Number.isFinite(maxZ) ? maxZ : (Number.isFinite(m.maxZ) ? m.maxZ : 1));
			if (Number.isFinite(maxZ) && maxZ > unionMaxZ) unionMaxZ = maxZ;
		}
		if (!Number.isFinite(minX)) { minX = -1; maxX = 1; minY = -1; maxY = 1; }
		if (!Number.isFinite(unionMaxZ) || unionMaxZ <= 0) unionMaxZ = 1;
		const cx = (minX + maxX) / 2;
		const cy = (minY + maxY) / 2;
		const extentX = Math.max(maxX - minX, 1e-3);
		const extentY = Math.max(maxY - minY, 1e-3);
		const pitch = this.opts.pitch;
		let cellHalfExtent: number;

		if (pitch >= 89.999) {
			// 2D top-down path — EXACTLY as before (byte-identical). The camera is already set up
			// top-down in ensureScratch; re-assert the orientation here (robust to a prior pitched
			// bake in the same baker instance) + frame by the ground-plane extent only.
			camera.up.set(0, 1, 0);
			camera.position.set(0, 0, 120);
			camera.lookAt(0, 0, 0);
			cellHalfExtent = Math.max(extentX, extentY) * 0.52; // ~90% fill with margin
			camera.left = cx - cellHalfExtent;
			camera.right = cx + cellHalfExtent;
			camera.top = cy + cellHalfExtent;
			camera.bottom = cy - cellHalfExtent;
			camera.updateProjectionMatrix();
		} else {
			// 3D pitched path — the tree viewed at `pitch` (degrees below horizontal). up = +z so
			// the tree is UPRIGHT on screen (a cylindrical billboard's vertical). The cell is
			// SQUARE in world units; its half-extent is the LARGER of the crown's horizontal half-extent
			// and HALF the tree height (so a wide-short tree fills the cell width, a tall-narrow
			// tree fills the cell height). The GROUND (z=0) sits at the cell bottom and the crown
			// top near the cell top. The image plane vertical is FORESHORTENED by cos(pitch)
			// (world-z s → screen-y s·cos(pitch); at 90°=top-down cos=1 no foreshorten; at 0°=edge-on
			// cos≈1 the 17m height projects to ~17m). top/bottom = ±cellHalfExtent·cos(pitch)
			// frames world-z [0, 2·cellHalfExtent]; left/right = ±cellHalfExtent frames world-x
				// [cx±cellHalfExtent] with NO compression. A SQUARE billboard quad of half-extent
			// `cellHalfExtent` then maps the cell to a true-sized tree.
			const crownHalf = Math.max(extentX, extentY) * 0.5;
			cellHalfExtent = Math.max(crownHalf, unionMaxZ * 0.5) * 1.05; // HALF the tree height (not full)
			const cosP = Math.max(1e-3, Math.cos(pitch * Math.PI / 180));
			const vertHalf = cellHalfExtent * cosP; // world-z [0,2·cellHalfExtent] → screen-y ±this
			// Re-orient the camera: up = +z (vertical). Projection centre (view plane centre) sits
			// at world-z = cellHalfExtent, so the cell spans world-z [0, 2·cellHalfExtent] (ground
			// at the bottom). Look down at `pitch` toward +y (north) — the camera stands behind the
			// tree and looks at it. left/right/top/bottom are in CAMERA-LOCAL units offset from
			// the camera's own position, so they're centred at 0 (the camera sits at (cx, cy, …) —
			// world-x cx maps to local-x 0; world-z cellHalfExtent maps to local-y 0).
			camera.up.set(0, 0, 1);
			camera.position.set(cx, cy, cellHalfExtent);
			camera.lookAt(cx, cy + 100, cellHalfExtent - 100 * Math.tan(pitch * Math.PI / 180));
			camera.left = -cellHalfExtent;
			camera.right = +cellHalfExtent;
			camera.top = +vertHalf;
			camera.bottom = -vertHalf;
			camera.updateProjectionMatrix();
		}

		// Set the light once (shared across all variants). §4.5: `AmbientLight(intensity=ambient)`,
		// `DirectionalLight(intensity=strength)` positioned at `bakeLightDir(rig) * d` (the light
		// direction) so it shines toward the origin — the terrain's `computeLightDir` convention.
		const [lx, ly, lz] = bakeLightDir(light);
		this.directional!.position.set(lx, ly, lz).multiplyScalar(100);
		this.directional!.intensity = light.strength;
		this.ambient!.intensity = light.ambient;

		// Offscreen atlas: transparent clear (alpha 0) + mesh alpha 1 → coverage = alpha > 0.
		const atlas = new THREE.WebGLRenderTarget(atlasW, atlasH, {
			type: THREE.UnsignedByteType,
			depthBuffer: true,
			stencilBuffer: false,
		});
		this.renderer.setRenderTarget(atlas);
		this.renderer.setClearColor(new THREE.Color(0, 0, 0), 0);
		this.renderer.clear(true, true, true);

		const coverage: number[] = [];
		const pixels = new Uint8ClampedArray(atlasW * atlasH * 4);
		timeIt("bake.renderLoop", ()=>{
		for (let i = 0; i < n; i++) {
			const col = i % cols;
			const row = Math.floor(i / cols);
			const vx = col * cellSize;
			const vy = row * cellSize;

			// Set14 P4 §4.5 — render each PART with its material kind's MeshStandardMaterial
			// (bark: opaque FrontSide; leaf: alpha-tested DoubleSide). Both into the SAME cell so
			// the depth buffer orders them (the canopy over the trunk). Materials are built
			// per-variant (a swung `barkTint` etc. is honoured); the per-part geometry is swapped
			// onto the shared mesh objects and disposed after render. Set15 P7 — the materials
			// come from the SHARED `buildSceneryMaterials` (promoted to SceneryMaterials.ts), the
			// same builder the 3D near tier uses.
			// TEMP DEBUG (2026-09-01): BAKE_SOLID_RED_DEBUG skips the mesh render and fills the
			// cell solid red (via the clear color) so any billboard that renders shows red.
			if (BAKE_SOLID_RED_DEBUG) {
				this.renderer.setViewport(vx, vy, cellSize, cellSize);
				this.renderer.setClearColor(new THREE.Color(1, 0, 0), 1);
				this.renderer.clear(true, true, true);
			} else {
				const mats = buildSceneryMaterials(variants[i].params);
				this.renderer.setViewport(vx, vy, cellSize, cellSize);
				for (const part of meshes[i].parts) {
					const isBark = part.materialKind == "bark";
					const meshObj = isBark ? barkMesh : leafMesh;
					const geo = partToGeometry(part);
					meshObj.geometry = geo;
					meshObj.material = isBark ? mats.bark : mats.leaf;
					this.renderer.render(this.scene!, camera);
					geo.dispose();
				}
				mats.bark.dispose();
				mats.leaf.dispose();
			}

			// Read this cell back.
			const cellBuf = timeIt("bake.readPixels", ()=>{
				const buf = new Uint8Array(cellSize * cellSize * 4);
				this.renderer.readRenderTargetPixels(atlas, vx, vy, cellSize, cellSize, buf);
				return buf;
			});
			// In SOLID_RED mode, overwrite the readback buf with solid red (the clear color above
			// fills the render target, but readRenderTargetPixels can return stale clears in
			// some driver configs — write the red directly to be certain).
			if (BAKE_SOLID_RED_DEBUG) for (let j = 0; j < cellBuf.length; j += 4) { cellBuf[j]=255; cellBuf[j+1]=0; cellBuf[j+2]=0; cellBuf[j+3]=255; }
			// (texture row 0 = top = world +Y/north). So flip WITHIN the cell's own row band —
			// read row r (bottom) → buffer row (vy + (cellSize-1-r)).
			for (let r = 0; r < cellSize; r++) {
				const srcOff = r * cellSize * 4;
				const dstRow = vy + (cellSize - 1 - r);
				const dstOff = dstRow * atlasW * 4 + vx * 4;
				for (let j = 0; j < cellSize * 4; j++) pixels[dstOff + j] = cellBuf[srcOff + j];
			}
			let covered = 0;
			for (let j = 3; j < cellBuf.length; j += 4) if (cellBuf[j] > 0) covered++;
			coverage.push(covered / (cellSize * cellSize));
		}
		});

		this.renderer.setRenderTarget(null);
		// Restore the viewport/scissor/pixelRatio AND the output config we captured before the
		// bake, so the next live frame renders into the FULL canvas again on its own settings.
		this.renderer.setViewport(savedViewport);
		this.renderer.setScissor(savedScissor);
		this.renderer.setScissorTest(savedScissorTest);
		this.renderer.setPixelRatio(savedPixelRatio);
		this.renderer.toneMapping = savedToneMapping;
		this.renderer.outputColorSpace = savedOutputColorSpace;
		atlas.dispose();

		// The cell's VERTICAL world half-extent (what the billboard quad's Z scale uses). At
		// pitch=90 (top-down) cos=1 → equals cellHalfExtent (square cell, byte-identical 2D).
		// At low pitch the tree's 17m height is barely foreshortened (cos(1°)=0.9998 ≈ 1), so the
		// vertical cell ≈ cellHalfExtent — the tree fills it. (The billboard quad is non-square
		// only via the X/Z distinction; the vertical tracks cos(pitch).)
		const cosP = Math.max(1e-3, Math.cos(pitch * Math.PI / 180));

		return {
			signature: sceneryBakeSignature(resolved, light, this.opts),
			cellSize, cols, rows, atlasWidth: atlasW, atlasHeight: atlasH,
			pixels, variants, coverage,
			cellWorldHalfExtent: cellHalfExtent, cellWorldHalfExtentZ: pitch >= 89.999 ? cellHalfExtent : cellHalfExtent * cosP,
			variantMaxZ,
		};
	}

	private ensureScratch(): void {
		if (this.scene) return;
		this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 400);
		// Z-up: the mesh's ground plane is X/Y, up is +Z, so a plain top-down ortho looks down -Z
		// with +Y up on screen (matching the map's world → screen mapping and the quad pass).
		this.camera.up.set(0, 1, 0);
		this.camera.position.set(0, 0, 120);
		this.camera.lookAt(0, 0, 0);
		this.scene = new THREE.Scene();
		this.directional = new THREE.DirectionalLight(0xffffff, 1);
		this.ambient = new THREE.AmbientLight(0xffffff, 0.2);
		this.scene.add(this.directional, this.ambient);
		this.barkMesh = new THREE.Mesh(undefined as unknown as THREE.BufferGeometry, undefined as unknown as THREE.Material);
		this.leafMesh = new THREE.Mesh(undefined as unknown as THREE.BufferGeometry, undefined as unknown as THREE.Material);
		this.scene.add(this.barkMesh, this.leafMesh);
	}
}
